// --------------------------------------------------------------------------
// แพ็คเสริม (ปั้มโซเชียล + point / ฟีเจอร์เสริม / เพิ่มโควตา)
// --------------------------------------------------------------------------
// point
//   - ยอดคงเหลือ = ผลรวม points ในชีต boost_points ของร้าน (ไม่เก็บยอดซ้ำไว้ที่อื่น กันยอดเพี้ยน)
//   - เติม = +points (กันบันทึกซ้ำด้วย transactionId) · สั่งปั้ม = -points · ยกเลิก = +points คืน
//   - ข้อจำกัด: ชีตไม่มี transaction — กดสั่งพร้อมกันหลายแท็บอาจใช้เกินยอดได้ ต้องย้ายไป Backend จริงถึงจะกันได้
//
// ออเดอร์ปั้ม (boost_orders)
//   - สร้างเป็น pending แล้วทีมงานทำให้เอง เปลี่ยน status ในชีต: in_progress -> completed
//   - ยกเลิกได้เฉพาะ pending (ร้านกดเอง) หรือทีมงานตั้ง cancelled ในชีต -> ร้านกด "รับ point คืน" ได้
//
// ฟีเจอร์เสริมของแพ็กเกจ
//   - เก็บที่แถว organizations.addons = [{ id, kind: module|quota, key, amount, expireAt, purchaseId }]
//   - หมดอายุพร้อมแพ็กเกจหลัก ณ วันที่ซื้อ — ต่ออายุแพ็กเกจหลักแล้วต้องกดต่อแพ็คเสริมเอง
// --------------------------------------------------------------------------
import { ApiError } from "./apiClient";
import {
  organizationRepo, boostOrderRepo, boostPointRepo, addonPurchaseRepo, customRequestRepo, documentRepo, slipRepo,
} from "../mock/repositories";
import { storageUsedBytes } from "../config/storage";
import {
  API_ACCESS_KIND, CUSTOM_KIND, INVALID_QUOTE_MESSAGE, canCancelRequest, parseLinks, quotePayable, validateRequestForm,
} from "../config/custom-request";
import {
  BOOST_QTY,
  BOOST_SERVICE_MAP,
  BOOST_PLATFORM_MAP,
  POINT_PACK_MAP,
  QUOTA_ADDON_MAP,
  MODULE_ADDON_PRICES,
  SERVICE_MAP,
  boostCost,
  serviceTitle,
  pointsForAmount,
  validBoostUrl,
} from "../config/addons";

const newId = (prefix) => `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const orderNoOf = () => {
  const now = new Date();
  const ymd = `${String(now.getFullYear()).slice(2)}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  return `BST-${ymd}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
};

const newestFirst = (rows) => [...rows].sort((a, b) => new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0));

const sumPoints = (rows) => rows.reduce((total, row) => total + (Number(row.points) || 0), 0);

// ชีต boost_points เก็บ 2 กระเป๋า: point ปั้ม (wallet ว่าง) และเครดิต AI (wallet = "ai")
const isAiWallet = (row) => row.wallet === "ai";

export const boostService = {
  /** ยอด point คงเหลือของร้าน (ไม่รวมเครดิต AI) */
  balance: async (orgId) => {
    if (!orgId) return 0;
    return sumPoints(await boostPointRepo.all(orgId, (row) => !isAiWallet(row)));
  },

  /** สมุดบัญชี point (ใหม่สุดก่อน · ไม่รวมเครดิต AI) */
  ledger: async (orgId) => (orgId ? newestFirst(await boostPointRepo.all(orgId, (row) => !isAiWallet(row))) : []),

  /** ออเดอร์ปั้มทั้งหมดของร้าน (ใหม่สุดก่อน) */
  orders: async (orgId) => (orgId ? newestFirst(await boostOrderRepo.all(orgId)) : []),

  /**
   * บันทึก point หลังจ่าย QR สำเร็จ — เรียกซ้ำได้ (กดลองใหม่จากโมดัล) ไม่เติมซ้ำ
   * ส่ง packKey (แพ็คสำเร็จรูป) หรือ amount (เติมเอง) อย่างใดอย่างหนึ่ง
   * idempotencyKey (ไม่บังคับ) — ใช้กับตะกร้าที่ 1 transaction มีหลายบรรทัด ถ้าส่งมาจะกันซ้ำด้วย key นี้แทน transactionId
   */
  topup: async ({ orgId, packKey, amount, transaction, by = "", idempotencyKey = "" }) => {
    if (!orgId) throw new ApiError("ไม่พบข้อมูลร้าน");
    const pack = packKey ? POINT_PACK_MAP[packKey] : null;
    if (packKey && !pack) throw new ApiError("ไม่พบแพ็คที่เลือก");

    const paid = pack ? pack.price : Math.floor(Number(amount) || 0);
    const points = pack ? pack.points : pointsForAmount(paid);
    if (!points) throw new ApiError("ยอดเติมไม่ถูกต้อง");

    const transactionId = transaction?.transactionId || "";
    if (idempotencyKey) {
      const existing = await boostPointRepo.all(orgId, (row) => row.idempotencyKey === idempotencyKey);
      if (existing.length) return existing[0];
    } else if (transactionId) {
      const existing = await boostPointRepo.all(orgId, (row) => row.transactionId === transactionId);
      if (existing.length) return existing[0];
    }

    return boostPointRepo.create(orgId, {
      type: "topup",
      ...(pack?.wallet ? { wallet: pack.wallet } : {}),
      points,
      amount: paid,
      packKey: pack?.key || "",
      detail: pack ? pack.name : `เติม ${paid.toLocaleString()} บาท`,
      transactionId,
      ...(idempotencyKey ? { idempotencyKey } : {}),
      refNo: transaction?.refNo || "",
      by,
      createdAt: new Date().toISOString(),
    });
  },

  /** สั่งปั้ม — ตัด point ก่อนแล้วค่อยสร้างออเดอร์ ถ้าสร้างออเดอร์พังจะคืน point ให้ทันที */
  order: async ({ orgId, serviceKey, targetUrl, quantity, note = "", by = "" }) => {
    if (!orgId) throw new ApiError("ไม่พบข้อมูลร้าน");
    const service = BOOST_SERVICE_MAP[serviceKey];
    if (!service) throw new ApiError("ไม่พบบริการที่เลือก");

    const url = String(targetUrl || "").trim();
    if (!validBoostUrl(service.platform, url)) {
      throw new ApiError(`ลิงก์ต้องเป็นของ ${BOOST_PLATFORM_MAP[service.platform].name} (ขึ้นต้น https://)`);
    }

    const qty = Number(quantity) || 0;
    if (qty < BOOST_QTY.min || qty > BOOST_QTY.max || qty % BOOST_QTY.step !== 0) {
      throw new ApiError(`จำนวนต้องอยู่ระหว่าง ${BOOST_QTY.min.toLocaleString()} - ${BOOST_QTY.max.toLocaleString()} และเป็นหลักร้อย`);
    }

    const cost = boostCost(service.key, qty);
    const balance = await boostService.balance(orgId);
    if (balance < cost) throw new ApiError(`point ไม่พอ (ต้องใช้ ${cost.toLocaleString()} มี ${balance.toLocaleString()})`);

    const orderId = newId("bst");
    const orderNo = orderNoOf();
    const now = new Date().toISOString();
    const typeName = service.type === "like" ? "ไลค์" : "ฟอลโลว์";
    const detail = `${BOOST_PLATFORM_MAP[service.platform].name} ${typeName} ${qty.toLocaleString()}`;

    await boostPointRepo.create(orgId, { type: "spend", points: -cost, orderId, orderNo, detail, by, createdAt: now });

    try {
      return await boostOrderRepo.create(orgId, {
        id: orderId,
        orderNo,
        serviceKey: service.key,
        platform: service.platform,
        type: service.type,
        targetUrl: url,
        quantity: qty,
        rate: service.rate,
        points: cost,
        status: "pending",
        note: String(note || "").slice(0, 300),
        by,
        createdAt: now,
      });
    } catch (err) {
      await boostPointRepo.create(orgId, { type: "refund", points: cost, orderId, orderNo, detail: `คืน point (สร้างออเดอร์ไม่สำเร็จ) ${detail}`, createdAt: new Date().toISOString() });
      throw err;
    }
  },

  /**
   * ยกเลิกออเดอร์ + คืน point — เรียกซ้ำได้ ไม่คืนซ้ำ
   * pending -> ร้านยกเลิกเองได้ · cancelled (ทีมงานยกเลิกในชีต) -> แค่คืน point ที่ยังไม่ได้คืน
   */
  cancel: async (orgId, orderId) => {
    const order = await boostOrderRepo.get(orgId, orderId);
    if (!order) throw new ApiError("ไม่พบออเดอร์");
    if (order.status !== "pending" && order.status !== "cancelled") {
      throw new ApiError("ทีมงานเริ่มดำเนินการแล้ว ยกเลิกไม่ได้");
    }

    if (order.status === "pending") await boostOrderRepo.update(orgId, order.id, { status: "cancelled", cancelledAt: new Date().toISOString() });

    const refunded = await boostPointRepo.all(orgId, (row) => row.type === "refund" && row.orderId === order.id);
    if (!refunded.length) {
      await boostPointRepo.create(orgId, {
        type: "refund",
        points: Number(order.points) || 0,
        orderId: order.id,
        orderNo: order.orderNo,
        detail: `คืน point ออเดอร์ ${order.orderNo}`,
        createdAt: new Date().toISOString(),
      });
    }
    return boostOrderRepo.update(orgId, order.id, { refundedAt: new Date().toISOString() });
  },
};

// --------------------------------------------------------------------------
// ฟีเจอร์เสริม / เพิ่มโควตา
// --------------------------------------------------------------------------
// เครดิต AI — ซื้อผ่าน boostService.topup (แพ็ค group "ai") ยังไม่มีการตัดเครดิตจนกว่าฟีเจอร์ AI จะเปิดใช้งาน
export const aiCreditService = {
  balance: async (orgId) => (orgId ? sumPoints(await boostPointRepo.all(orgId, isAiWallet)) : 0),
  ledger: async (orgId) => (orgId ? newestFirst(await boostPointRepo.all(orgId, isAiWallet)) : []),
};

export const addonService = {
  history: async (orgId) => (orgId ? newestFirst(await addonPurchaseRepo.all(orgId)) : []),

  /** ราคา/เดือน ของรายการ — module: โมดูลละ 1 · quota: ราคาต่อก้อน × จำนวนก้อน */
  monthlyPrice: ({ kind, key, blocks = 1 }) => {
    if (kind === "module") return MODULE_ADDON_PRICES[key] || 0;
    if (kind === "quota") return (QUOTA_ADDON_MAP[key]?.price || 0) * Math.max(1, Number(blocks) || 1);
    return 0;
  },

  /**
   * บันทึกหลังจ่าย QR สำเร็จ — ซื้อใหม่ หรือต่ออายุ (renewId = id ของแพ็คเสริมเดิม)
   * หมดอายุพร้อมแพ็กเกจหลัก · เรียกซ้ำได้ (เช็ค transactionId) ไม่เพิ่มซ้ำ
   * idempotencyKey (ไม่บังคับ) — ตะกร้า: 1 transaction หลายบรรทัด ใช้ key ต่อบรรทัดแทน transactionId
   */
  activate: async ({ organization, kind, key, blocks = 1, renewId = "", name = "", amount, days, transaction, by = "", idempotencyKey = "" }) => {
    if (!organization?.id) throw new ApiError("ไม่พบข้อมูลร้าน");
    if (kind === "module" && !MODULE_ADDON_PRICES[key]) throw new ApiError("โมดูลนี้ซื้อแยกไม่ได้");
    if (kind === "quota" && !QUOTA_ADDON_MAP[key]) throw new ApiError("ไม่พบฟีเจอร์เสริมที่เลือก");

    // อ่านแถวร้านล่าสุดก่อนเขียน กันทับแพ็คเสริมที่เพิ่งซื้อจากอีกแท็บ
    const fresh = (await organizationRepo.get(null, organization.id)) || organization;
    const expireAt = fresh.planExpireAt;
    if (!expireAt || new Date(expireAt) <= new Date()) throw new ApiError("แพ็กเกจหลักหมดอายุแล้ว กรุณาต่ออายุแพ็กเกจก่อน");

    const addons = Array.isArray(fresh.addons) ? [...fresh.addons] : [];
    const transactionId = transaction?.transactionId || "";
    const matchKey = idempotencyKey
      ? (row) => row.idempotencyKey === idempotencyKey
      : transactionId
      ? (row) => row.transactionId === transactionId
      : null;
    const already = matchKey ? (await addonPurchaseRepo.all(organization.id, matchKey))[0] : null;

    const quantity = kind === "quota" ? QUOTA_ADDON_MAP[key].block * Math.max(1, Number(blocks) || 1) : 1;
    const entryId = renewId || already?.addonId || newId("add");
    const now = new Date().toISOString();

    if (!already) {
      await addonPurchaseRepo.create(organization.id, {
        addonId: entryId,
        kind,
        key,
        quantity,
        name,
        action: renewId ? "renew" : "buy",
        days: Number(days) || 0,
        amount: Number(amount) || 0,
        expireAt,
        status: "paid",
        transactionId,
        ...(idempotencyKey ? { idempotencyKey } : {}),
        refNo: transaction?.refNo || "",
        by,
        createdAt: now,
      });
    }

    const index = addons.findIndex((row) => row.id === entryId);
    if (index >= 0) {
      addons[index] = { ...addons[index], expireAt, renewedAt: now };
    } else {
      addons.push({ id: entryId, kind, key, amount: quantity, expireAt, purchasedAt: now });
    }

    // ซื้อโมดูลแล้วเปิดใช้ให้เลย ไม่ต้องไปกดเปิดที่หน้าจัดการโมดูลอีกรอบ
    const patch = { addons };
    if (kind === "module") patch.enabledModules = Array.from(new Set([...(fresh.enabledModules || []), key]));

    return organizationRepo.update(null, organization.id, patch);
  },
};

// --------------------------------------------------------------------------
// ฟีเจอร์ custom / คำขอเปิดใช้ API (ชีต custom_requests)
// --------------------------------------------------------------------------
//   - ร้านสร้างคำขอ -> pending_quote · ทีมงานกรอกใบเสนอราคาในชีต (mandays, ratePerManday, amount, quoteNote, quotedAt) แล้วตั้ง status=quoted
//   - หน้าเว็บตรวจใบเสนอราคา (config/custom-request.js) ทุกครั้งก่อนให้จ่าย และตอนบันทึกหลังจ่าย
//   - จ่ายสำเร็จ -> paid (กันซ้ำด้วย idempotencyKey / transactionId) · ทีมงานเปลี่ยน in_progress / done ในชีต
//   - kind = custom (ค่าเริ่มต้น) | api_access (ขอเปิดใช้ API — ไม่มีการเก็บเงินในระบบนี้ ทีมงานติดต่อกลับเอง)
// --------------------------------------------------------------------------
const requestNoOf = (prefix) => {
  const now = new Date();
  const ymd = `${String(now.getFullYear()).slice(2)}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  return `${prefix}-${ymd}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
};

export const customRequestService = {
  /** ทุกคำขอของร้าน (ใหม่สุดก่อน) */
  list: async (orgId) => (orgId ? newestFirst(await customRequestRepo.all(orgId)) : []),

  /** เฉพาะฟีเจอร์ custom (ไม่รวมคำขอ API) */
  /** อ่านแถวล่าสุดจากชีต (ใช้ตรวจใบเสนอราคาก่อนจ่าย) */
  get: async (orgId, id) => {
    if (!orgId || !id) throw new ApiError("ไม่พบข้อมูลคำขอ");
    const row = await customRequestRepo.get(orgId, id);
    if (!row) throw new ApiError("ไม่พบคำขอ");
    return row;
  },

  listCustom: async (orgId) => (await customRequestService.list(orgId)).filter((row) => (row.kind || CUSTOM_KIND) === CUSTOM_KIND),

  listApi: async (orgId) => (await customRequestService.list(orgId)).filter((row) => row.kind === API_ACCESS_KIND),

  create: async ({ orgId, title, description, wantedAt = "", linksText = "", attachment = null, by = "" }) => {
    if (!orgId) throw new ApiError("ไม่พบข้อมูลร้าน");
    const message = validateRequestForm({ title, description, wantedAt, linksText, attachBytes: attachment?.sizeBytes || 0 });
    if (message) throw new ApiError(message);
    const { links } = parseLinks(linksText);
    return customRequestRepo.create(orgId, {
      kind: CUSTOM_KIND,
      requestNo: requestNoOf("CR"),
      title: String(title).trim(),
      description: String(description).trim(),
      wantedAt: wantedAt || "",
      links,
      attachment: attachment ? { name: String(attachment.name || "").slice(0, 120), sizeBytes: attachment.sizeBytes, dataUrl: attachment.dataUrl } : null,
      sizeBytes: attachment?.sizeBytes || 0,
      status: "pending_quote",
      by,
      createdAt: new Date().toISOString(),
    });
  },

  /**
   * สั่งซื้อบริการติดตั้ง/อบรม (ราคาคงที่) — สร้างคำขอพร้อมใบเสนอราคา (quoted) แล้วให้จ่ายผ่าน flow ใบเสนอราคาเดิม
   * ยังมีคำขอบริการเดียวกันที่รอจ่าย -> คืนแถวเดิม (ไม่สร้างซ้ำ)
   */
  orderService: async ({ orgId, serviceKey, by = "" }) => {
    if (!orgId) throw new ApiError("ไม่พบข้อมูลร้าน");
    const service = SERVICE_MAP[serviceKey];
    if (!service) throw new ApiError("ไม่พบบริการที่เลือก");
    const title = serviceTitle(service);
    const open = (await customRequestService.listCustom(orgId)).find((row) => row.title === title && row.status === "quoted");
    if (open) return open;
    const now = new Date().toISOString();
    return customRequestRepo.create(orgId, {
      kind: CUSTOM_KIND,
      requestNo: requestNoOf("SV"),
      title,
      description: `${service.name} (${service.detail})`,
      serviceKey: service.key,
      links: [],
      attachment: null,
      sizeBytes: 0,
      mandays: service.mandays,
      ratePerManday: service.rate,
      amount: service.mandays * service.rate,
      quoteNote: "ราคามาตรฐานของบริการ",
      quotedAt: now,
      status: "quoted",
      by,
      createdAt: now,
    });
  },

  /** ขอเปิดใช้ API — บันทึกคำขอเท่านั้น (ไม่มี API key จริงในระบบนี้) กันกดซ้ำถ้ายังมีคำขอค้างอยู่ */
  requestApiAccess: async ({ orgId, note = "", contact = "", by = "" }) => {
    if (!orgId) throw new ApiError("ไม่พบข้อมูลร้าน");
    const open = (await customRequestService.listApi(orgId)).find((row) => row.status === "pending_quote" || row.status === "in_progress");
    if (open) throw new ApiError("มีคำขอเปิดใช้ API ที่ทีมงานกำลังดำเนินการอยู่แล้ว");
    return customRequestRepo.create(orgId, {
      kind: API_ACCESS_KIND,
      requestNo: requestNoOf("API"),
      title: "ขอเปิดใช้ API",
      description: String(note || "").trim().slice(0, 1000),
      contact: String(contact || "").trim().slice(0, 200),
      status: "pending_quote",
      by,
      createdAt: new Date().toISOString(),
    });
  },

  /** ร้านยกเลิกเองได้ก่อนจ่าย (pending_quote / quoted) — อ่านแถวล่าสุดก่อนเขียน กันยกเลิกหลังทีมงานเริ่มงาน/จ่ายแล้ว */
  cancel: async (orgId, id) => {
    const fresh = await customRequestRepo.get(orgId, id);
    if (!fresh) throw new ApiError("ไม่พบคำขอ");
    if (fresh.status === "cancelled") return fresh;
    if (fresh.kind === API_ACCESS_KIND ? fresh.status !== "pending_quote" : !canCancelRequest(fresh)) {
      throw new ApiError("ชำระเงินหรือทีมงานเริ่มดำเนินการแล้ว ยกเลิกไม่ได้");
    }
    return customRequestRepo.update(orgId, id, { status: "cancelled", cancelledAt: new Date().toISOString() });
  },

  /**
   * บันทึกหลังจ่าย QR สำเร็จ — เรียกซ้ำได้ (ปุ่มลองใหม่ของโมดัล) ไม่บันทึกซ้ำ
   * ตรวจซ้ำกับแถวล่าสุดในชีต: ต้องยัง quoted + ใบเสนอราคาผ่าน + ยอดที่จ่ายตรงกับ amount ในใบเสนอราคา
   * ถ้าไม่ตรง (ทีมงานแก้ใบเสนอราคาระหว่างรอจ่าย) จะไม่ตั้งเป็น paid และบอกให้ติดต่อทีมงานพร้อมเลขอ้างอิง
   */
  markPaid: async ({ orgId, id, amount, transaction, by = "", idempotencyKey = "" }) => {
    if (!orgId || !id) throw new ApiError("ไม่พบข้อมูลคำขอ");
    const fresh = await customRequestRepo.get(orgId, id);
    if (!fresh) throw new ApiError("ไม่พบคำขอ");

    const transactionId = transaction?.transactionId || "";
    const key = idempotencyKey || (transactionId ? `${transactionId}:custom:${id}` : "");
    if (key && fresh.idempotencyKey === key) return fresh; // บันทึกไปแล้ว (เรียกซ้ำ)
    if (!key && transactionId && fresh.transactionId === transactionId) return fresh;

    const ref = transaction?.refNo ? ` (อ้างอิง ${transaction.refNo})` : "";
    const check = quotePayable(fresh);
    if (!check.ok) {
      throw new ApiError(
        `${fresh.status === "quoted" ? INVALID_QUOTE_MESSAGE : "คำขอนี้ไม่อยู่ในสถานะรอชำระเงิน"}${ref} — หากชำระเงินแล้วโปรดแจ้งทีมงานพร้อมเลขอ้างอิง`
      );
    }
    if (Number(amount) !== check.amount) {
      throw new ApiError(`ยอดที่ชำระไม่ตรงกับใบเสนอราคาปัจจุบัน${ref} — โปรดแจ้งทีมงานพร้อมเลขอ้างอิง`);
    }

    return customRequestRepo.update(orgId, id, {
      status: "paid",
      paidAt: new Date().toISOString(),
      paidAmount: check.amount,
      transactionId,
      ...(key ? { idempotencyKey: key } : {}),
      refNo: transaction?.refNo || "",
      paidBy: by,
    });
  },
};

// --------------------------------------------------------------------------
// ที่เก็บไฟล์: พื้นที่ที่ใช้ (ไบต์) = documents + slips + ไฟล์แนบ custom_requests
// --------------------------------------------------------------------------
export const storageService = {
  usedBytes: async (orgId) => {
    if (!orgId) return 0;
    const [documents, slips, requests] = await Promise.all([documentRepo.all(orgId), slipRepo.all(orgId), customRequestRepo.all(orgId)]);
    // คำขอที่ยกเลิกแล้วยังเก็บไฟล์อยู่ในชีต จึงนับด้วย
    return storageUsedBytes(documents, slips, requests);
  },
};
